import { z } from 'zod/v4';

import { ThemeColorNames } from '@sites/site-theme';

import { normalizeFlatArgs, parseIfJsonText } from '../widget-schemas/fold-helpers';

/**
 * AI-facing schema for `update_theme_colors` — the site's five-slot palette.
 *
 * **Why the palette is the first theme thing the AI can write.** `get_page_snapshot` has reported
 * the palette since it existed, for a measured reason (`theme-summary.ts`): asked for a block, the
 * model invented `#1D4ED8`/`#DBEAFE`/`#fbbf24` — a coherent palette with nothing to do with the site.
 * Reading it fixed half of that. The other half is a user who says "make the site warmer": until
 * now the only thing the model could do was recolour every block it could reach, one entity at a
 * time, leaving every block it could not reach behind. One palette write moves the whole site,
 * including the parts the AI never touched, because every entity that names a colour resolves it
 * through these five slots.
 *
 * **Five named fields, not a list of `{name, value}` pairs.** The vocabulary is closed
 * (`ThemeColorNames`), so spelling it out puts the slot names in `tools/list` where the model reads
 * them, and makes a typo a schema error instead of a silently-ignored entry. The pair form is still
 * ACCEPTED — see the fold — because it is what a model reaches for when it thinks in terms of the
 * snapshot's `colors: [{name, value}]` echo.
 */
export const THEME_COLOR_NAMES = Object.values(ThemeColorNames);

/** Where a nested palette can hide. The model mirrors the snapshot's own key more often than not. */
const PALETTE_CONTAINER_KEYS = ['colors', 'palette', 'theme', 'themeColors'] as const;

const colorField = (slot: ThemeColorNames, meaning: string) =>
    z
        .string()
        .optional()
        .describe(
            `${meaning} Any CSS colour — "#1D4ED8", "rgb(29,78,216)", "rgba(29,78,216,0.8)". ` +
                `Omit to leave "${slot}" as it is.`
        );

const updateThemeColorsObjectSchema = z.object({
    light: colorField(ThemeColorNames.light, 'The light slot — page backgrounds, and text on dark areas.'),
    dark: colorField(ThemeColorNames.dark, 'The dark slot — body text, and backgrounds of dark sections.'),
    primary: colorField(ThemeColorNames.primary, 'The primary brand colour — buttons and accents lead with it.'),
    secondary: colorField(ThemeColorNames.secondary, 'The secondary colour — supporting fills and softer areas.'),
    accent: colorField(ThemeColorNames.accent, 'The accent colour — highlights, badges, the occasional emphasis.'),
});

export type UpdateThemeColorsArgs = z.infer<typeof updateThemeColorsObjectSchema>;

/** `{ name: "primary", value: "#f00" }` → writes the `primary` slot. Ignores anything else. */
function liftPair(args: Record<string, unknown>, pair: unknown): void {
    if (!pair || typeof pair !== 'object' || Array.isArray(pair)) {
        return;
    }

    const { name, value } = pair as { name?: unknown; value?: unknown };

    if (typeof name === 'string' && THEME_COLOR_NAMES.includes(name as ThemeColorNames) && args[name] === undefined) {
        args[name] = value;
    }
}

/**
 * Lifts the shapes a model writes instead of the flat one. All of them were observed, none is a
 * guess.
 *
 * - `{ colors: { primary: "#f00" } }` — the snapshot reports the palette under `colors`, so
 *   answering in the same shape is the obvious move, and rejecting it teaches nothing.
 * - `{ colors: [{ name: "primary", value: "#f00" }] }` — the snapshot's palette is an ARRAY of
 *   pairs, so echoing it back whole is at least as natural as the map form. This used to be
 *   skipped explicitly (`!Array.isArray`), and it is the first thing the model tried.
 * - `{ name: "primary", value: "#f00" }` — one entry copied out of that array.
 * - **Any of the above as JSON TEXT.** The leaked-XML path has no types: `colors` arrives as the
 *   string `'[{"name":"primary","value":"rgb(43, 92, 153)"}]'`. Nothing here could see through
 *   that, so a model that had the right idea got an error twice and only landed on the third
 *   attempt with the flat form — three calls, two of them wasted, for one colour (2026-08-25).
 */
function foldPalette(args: Record<string, unknown>): void {
    for (const key of PALETTE_CONTAINER_KEYS) {
        const nested = parseIfJsonText(args[key]);

        if (Array.isArray(nested)) {
            nested.forEach((entry) => liftPair(args, entry));
        } else if (nested && typeof nested === 'object') {
            for (const slot of THEME_COLOR_NAMES) {
                const value = (nested as Record<string, unknown>)[slot];

                if (value !== undefined && args[slot] === undefined) {
                    args[slot] = value;
                }
            }

            // A container holding one pair rather than a map — `{ colors: { name, value } }`.
            liftPair(args, nested);
        }

        delete args[key];
    }

    liftPair(args, args);

    delete args['name'];
    delete args['value'];
}

export const updateThemeColorsArgsSchema = z.preprocess(
    (raw) => normalizeFlatArgs(raw, foldPalette),
    updateThemeColorsObjectSchema
);
